import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useCallback, type ChangeEvent } from "react";
import { type AddressFormState } from "../api-order-form";
import AddressFields from "./address-fields";
import ApiOrderSection from "./api-order-section";

const styles = {
  stopTypeField: {
    maxWidth: 260,
  } satisfies SxProps<Theme>,
};

type ApiOrderStopSectionProps = {
  /** Labels the checkbox that includes this stop, such as "Origin stop". */
  readonly label: string;
  readonly included: boolean;
  readonly stopTypeLabel: string;
  /**
   * The selected stop type. Typed as a string because origin and destination
   * draw from different enums; the caller narrows the value it receives back.
   */
  readonly stopType: string;
  readonly stopTypeOptions: readonly string[];
  readonly address: AddressFormState;
  readonly onIncludedChange: (next: boolean) => void;
  readonly onStopTypeChange: (next: string) => void;
  readonly onAddressChange: (next: AddressFormState) => void;
};

/**
 * One stop of the order: whether to create it, what type it is, and where it
 * is. Origin and destination differ only in their labels and their stop-type
 * enum, so both are rendered through this component.
 */
const ApiOrderStopSection = ({
  label,
  included,
  stopTypeLabel,
  stopType,
  stopTypeOptions,
  address,
  onIncludedChange,
  onStopTypeChange,
  onAddressChange,
}: ApiOrderStopSectionProps) => {
  const handleIncludedChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onIncludedChange(event.target.checked);
    },
    [onIncludedChange],
  );

  const handleStopTypeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onStopTypeChange(event.target.value);
    },
    [onStopTypeChange],
  );

  return (
    <ApiOrderSection>
      <FormControlLabel
        control={
          <Checkbox checked={included} onChange={handleIncludedChange} />
        }
        label={label}
      />
      {included && (
        <>
          <TextField
            select
            label={stopTypeLabel}
            size="small"
            sx={styles.stopTypeField}
            value={stopType}
            onChange={handleStopTypeChange}
          >
            {stopTypeOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <AddressFields value={address} onChange={onAddressChange} />
        </>
      )}
    </ApiOrderSection>
  );
};

export default ApiOrderStopSection;
